/**
 * External Imports
 */
import { FC, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { orderBy } from 'lodash';
import { format } from 'date-fns';

/**
 * Nexus Imports
 */
import { useAnalyticsApi } from '@shared-web-analytics/react/dist';

/**
 * Internal Imports
 */
import Table from '../../../shared/table';

import {
  logListClick,
  logRoundProfile
} from '../../../../utils/analyticsHelpers';
import { useDebounceValue } from '../../../../utils/debouncers';
import { date } from '../../../../utils/helpers';

/**
 * Global Type Definition Imports
 */
import { Employee } from '../../../../config/interfaces';

/**
 * Style Imports
 */
import {
  CellContainer,
  StyledLabel,
  StyledNeuTableRow
} from '../../../shared/table/Table.styles';
import {
  CompleteIcon,
  DeptStartContainer,
  EmployeeInfoDiv,
  IconDiv,
  NewHireIconContainer,
  OverdueImg,
  StartDateContainer,
  ToDoIcon
} from './EmployeeListTable.styles';

/**
 * Static File Imports
 */
import overdueLogo from '../../../../assets/images/alarm.png';

interface EmployeeTableProps {
  frequency?: string;
  isLoading: boolean;
  toDoRounds: Array<Employee>;
  completedRounds: Array<Employee>;
  newHireComplete: Array<Employee>;
  newHireToDo: Array<Employee>;
  searchTerm: string;
  selectedTab: string;
}

const EmployeesTable: FC<EmployeeTableProps> = ({
  frequency,
  isLoading,
  toDoRounds,
  completedRounds,
  newHireComplete,
  newHireToDo,
  searchTerm,
  selectedTab
}) => {
  const [sortKey, setSortKey] = useState<string>('endDueDate');
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('asc');
  const [active, setActive] = useState<string>('Round Due');

  const dbSearchTerm = useDebounceValue(searchTerm, 250);

  const { logTrackingEvent } = useAnalyticsApi();

  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { '*': view, type } = useParams();

  const handleSort = (column: string) => {
    let key;
    switch (column) {
      case 'Employee':
        key = 'fullName';
        break;
      case 'Department':
        key = 'dept';
        break;
      case 'Dept Start':
        key = 'effectiveStartDate';
        break;
      case 'Round Due':
        key = 'endDueDate';
        break;
      case 'Rounded By':
        key = 'lastRoundedBy';
        break;
      case 'Last Compliance Round':
        key = 'lastRoundDate';
        break;
      default:
        key = '';
    }
    if (key === sortKey && sortDir === 'asc') {
      setSortDir('desc');
    } else {
      setSortDir('asc');
    }
    setActive(column);
    setSortKey(key);
  };

  const openEmployeeProfile = (employee: Employee) => {
    logTrackingEvent([...logListClick('table', view), ...logRoundProfile()]);
    navigate('/employees/profile', {
      state: {
        from: pathname,
        employeeId: employee.hcaid
      }
    });
  };

  const filteredEmployees = useMemo(() => {
    const q = new RegExp(dbSearchTerm.trim().replace(/\\/g, ''), 'i');

    switch (selectedTab) {
      case 'To Do':
        return [
          toDoRounds.filter(
            employee =>
              `${employee.firstName} ${employee.lastName}`.match(q) ||
              employee.hcaid.match(q) ||
              `${employee.lastName} ${employee.firstName}`.match(q)
          ),
          []
        ];
      case 'Completed':
        return [
          [],
          completedRounds.filter(
            employee =>
              `${employee.firstName} ${employee.lastName}`.match(q) ||
              employee.hcaid.match(q) ||
              `${employee.lastName} ${employee.firstName}`.match(q)
          )
        ];
      case 'New Hires':
        return [
          newHireToDo.filter(
            employee =>
              `${employee.firstName} ${employee.lastName}`.match(q) ||
              employee.hcaid.match(q) ||
              `${employee.lastName} ${employee.firstName}`.match(q)
          ),
          newHireComplete.filter(
            employee =>
              `${employee.firstName} ${employee.lastName}`.match(q) ||
              employee.hcaid.match(q) ||
              `${employee.lastName} ${employee.firstName}`.match(q)
          )
        ];
      default:
        return [
          toDoRounds.filter(
            employee =>
              `${employee.firstName} ${employee.lastName}`.match(q) ||
              employee.hcaid.match(q) ||
              `${employee.lastName} ${employee.firstName}`.match(q)
          ),
          completedRounds.filter(
            employee =>
              `${employee.firstName} ${employee.lastName}`.match(q) ||
              employee.hcaid.match(q) ||
              `${employee.lastName} ${employee.firstName}`.match(q)
          )
        ];
    }
  }, [
    dbSearchTerm,
    toDoRounds,
    completedRounds,
    newHireToDo,
    newHireComplete,
    selectedTab
  ]);

  const sortedEmployees = useMemo(() => {
    const filteredEmployeesCopy = [...filteredEmployees];
    const orderBySortDir = sortDir === 'asc' ? 'asc' : 'desc';
    const iterateByLowercase = (employee: Employee) =>
      (employee as any)[sortKey]?.toLowerCase() || '';

    return [
      filteredEmployeesCopy[0].length
        ? orderBy(
            filteredEmployeesCopy[0],
            [iterateByLowercase],
            [orderBySortDir]
          )
        : filteredEmployeesCopy[0],
      filteredEmployeesCopy[1].length
        ? orderBy(
            filteredEmployeesCopy[1],
            [iterateByLowercase],
            [orderBySortDir]
          )
        : filteredEmployeesCopy[1]
    ];
  }, [filteredEmployees, sortDir, sortKey]);

  const isTableEmpty = useMemo(
    () =>
      !dbSearchTerm &&
      sortedEmployees.flat().length === 0 &&
      !(selectedTab === 'Completed'),
    [dbSearchTerm, sortedEmployees, selectedTab]
  );

  const isTableEmptyCompleted = useMemo(
    () =>
      !dbSearchTerm &&
      sortedEmployees.flat().length === 0 &&
      selectedTab === 'Completed',
    [dbSearchTerm, sortedEmployees, selectedTab]
  );

  const isTableEmptySearch = useMemo(
    () => !!dbSearchTerm && sortedEmployees.flat().length === 0,
    [dbSearchTerm, sortedEmployees]
  );

  const listView = useMemo(
    () => (type === 'delegate' ? 'delegateEmployeeList' : 'directEmployeeList'),
    [type]
  );

  const headers: JSX.Element[] = [
    <CellContainer
      key="Employee"
      id="Employee-Table-Employee-Column"
      data-testid="employee-table-employee-column"
      unit="column"
      colWidth={21}
      direction={!(active === 'Employee') ? 'none' : sortDir}
      sort
    >
      Employee
    </CellContainer>,
    <CellContainer
      key="Department"
      id="Employee-Table-Department-Column"
      data-testid="employee-table-department-column"
      unit="column"
      colWidth={20}
      direction={!(active === 'Department') ? 'none' : sortDir}
      sort
    >
      Department
    </CellContainer>,
    <CellContainer
      key="Dept Start"
      id="Employee-Table-Dept-Start-Column"
      data-testid="employee-table-dept-start-column"
      unit="column"
      colWidth={12}
      direction={!(active === 'Dept Start') ? 'none' : sortDir}
      sort
    >
      Dept Start
    </CellContainer>,
    <CellContainer
      key="Round Due"
      id="Employee-Table-Round-Due-Column"
      data-testid="employee-table-round-due-column"
      unit="column"
      colWidth={15}
      direction={!(active === 'Round Due') ? 'none' : sortDir}
      sort
    >
      Round Due
    </CellContainer>,
    <CellContainer
      key="Rounded By"
      id="Employee-Table-Rounded-By-Column"
      data-testid="employee-table-rounded-by-column"
      unit="column"
      colWidth={15}
      direction={!(active === 'Rounded By') ? 'none' : sortDir}
      sort
    >
      Rounded By
    </CellContainer>,
    <CellContainer
      key="Last Compliance Round"
      id="Employee-Table-Last-Round-Column"
      data-testid="employee-table-last-round-column"
      unit="column"
      colWidth={17}
      direction={!(active === 'Last Compliance Round') ? 'none' : sortDir}
      sort
    >
      Last Compliance Round
    </CellContainer>
  ];

  const rows = sortedEmployees.flat().map((employee: Employee, i: number) => {
    // const beginningOfYear = date.beginningOfYear();
    return (
      <StyledNeuTableRow
        // eslint-disable-next-line react/no-array-index-key
        key={`Employee-List-Row-${i}`}
        id={`Employee-List-Row-${employee.hcaid}`}
        data-testid={`Employee-List-Row-${employee.hcaid}`}
        unit="row"
        size="default"
        height="5em"
        role="button"
        styles={
          employee.complianceStatus === 'Completed' ||
          !employee.effectiveStartDate
            ? 'background-color: #f6f6f6 !important'
            : ''
        }
        onClick={
          employee.effectiveStartDate
            ? () => openEmployeeProfile(employee)
            : undefined
        }
      >
        <CellContainer unit="column" colWidth={21}>
          <EmployeeInfoDiv className="h-100 px-0">
            <IconDiv>
              {employee.complianceStatus === 'Completed' ? (
                <CompleteIcon className="neu-text-blue-80">check</CompleteIcon>
              ) : employee.complianceStatus === 'Overdue' ? (
                <OverdueImg width="20" alt="overdue logo" src={overdueLogo} />
              ) : (
                <ToDoIcon className="neu-text-orange-50">
                  {employee.effectiveStartDate ? 'circle' : ''}
                </ToDoIcon>
              )}
            </IconDiv>
            {/* <AvatarDiv>
              <NeuAvatar
                className="neu-text-gray-60 me-1"
                style={{ height: '40px', width: '40px' }}
              >
                {sasToken ? (
                  <EmployeeAvatar
                    src={
                      handleBlobPicUrl(employee.pictureUrl, sasToken) ??
                      userProfilePicUrl(employee.hcaid, sasToken)
                    }
                    alt="Employee Image"
                    onError={(e: any) => {
                      e.currentTarget.src = defaultImage;
                      return null;
                    }}
                  />
                ) : (
                  <EmployeeAvatar src={defaultImage} alt="Default Image" />
                )}
              </NeuAvatar>
            </AvatarDiv> */}
            <div>
              {employee.complianceStatus === 'Completed' ? (
                <StyledLabel className="neu-text-gray-100">
                  {employee.firstName} {employee.lastName}
                </StyledLabel>
              ) : (
                <StyledLabel className="neu-text-blue-70 bold">
                  {employee.firstName} {employee.lastName}
                </StyledLabel>
              )}
              <StyledLabel>{employee.hcaid}</StyledLabel>
            </div>
          </EmployeeInfoDiv>
        </CellContainer>
        <CellContainer unit="column" colWidth={20}>
          <StyledLabel className="neu-text-gray-90">
            {employee.dept}
          </StyledLabel>
        </CellContainer>
        <CellContainer unit="column" colWidth={12}>
          <DeptStartContainer>
            <StartDateContainer>
              {employee.effectiveStartDate &&
              employee.effectiveStartDate !== 'NULL'
                ? date.datePrettyString(employee.effectiveStartDate)
                : 'Pending'}
            </StartDateContainer>
            <NewHireIconContainer>
              {employee.newHire && (
                <CompleteIcon className="neu-text-blue-30 ms-0">
                  auto_awesome
                </CompleteIcon>
              )}
            </NewHireIconContainer>
          </DeptStartContainer>
        </CellContainer>
        <CellContainer unit="column" colWidth={15}>
          <StyledLabel>
            {employee.effectiveStartDate
              ? employee.newHire
                ? date.datePrettyString(employee.endDueDate)
                : frequency === 'monthly' || !employee.lastRoundDate
                ? date.alphanumericMonthYearDate(employee.startDueDate)
                : `${date.alphanumericMonthYearDate(
                    employee.startDueDate
                  )} - ${date.alphanumericMonthYearDate(employee.endDueDate)}`
              : ''}
          </StyledLabel>
        </CellContainer>
        <CellContainer unit="column" colWidth={15}>
          <StyledLabel>
            {employee.lastRoundDate &&
            employee.lastRoundedBy &&
            employee.lastRoundedBy !== 'Unknown'
              ? employee.lastRoundedBy
              : employee.lastRoundDate &&
                employee.lastRoundedBy &&
                employee.lastRoundedBy === 'Unknown'
              ? 'Unknown'
              : ''}
          </StyledLabel>
        </CellContainer>
        <CellContainer unit="column" colWidth={17}>
          <StyledLabel>
            {employee.lastRoundedBy &&
            employee.lastRoundedBy !== 'Unknown' &&
            !employee.lastRoundDate
              ? 'Unknown'
              : employee.lastRoundedBy &&
                employee.lastRoundDate &&
                date.isOrbitTimestamp(employee.lastRoundDate)
              ? format(date.parseDate(employee.lastRoundDate), 'MM/dd/yyyy') // make unknown if name and no date
              : ''}
          </StyledLabel>
        </CellContainer>
      </StyledNeuTableRow>
    );
  });

  return (
    <Table
      customClass="employee-list-table"
      headers={headers}
      rows={rows}
      isTableEmpty={isTableEmpty}
      isTableEmptySearch={isTableEmptySearch}
      isTableEmptyCompleted={isTableEmptyCompleted}
      loading={isLoading}
      selectedTab={selectedTab}
      type={listView}
      onSort={(e: any) => handleSort(e.detail.label)}
    />
  );
};

export default EmployeesTable;
